import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Megaphone, Plus, Play, Pause, Square, Trash2, Users, Phone, MessageSquare, Mail, ChevronRight, Clock, Target, Zap, ArrowLeft, Loader2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  hot_lead_blitz: { icon: Zap, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Hot Lead Blitz' },
  sms_drip: { icon: MessageSquare, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'SMS Drip' },
  email_drip: { icon: Mail, color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Email Drip' },
  multi_channel: { icon: Megaphone, color: 'text-violet-400', bg: 'bg-violet-500/10', label: 'Multi-Channel' },
};

const STATUS_BADGE = {
  draft: 'bg-secondary text-muted-foreground border-border',
  active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  paused: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  completed: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
};

const CHANNEL_ICON = { call: Phone, sms: MessageSquare, email: Mail };

export default function CampaignsPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | create | detail
  const [selected, setSelected] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/campaigns`, { headers });
      setCampaigns(data.campaigns || []);
    } catch { toast.error('Failed to load campaigns'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (view === 'create') return <CampaignBuilder headers={headers} onDone={() => { setView('list'); fetch(); }} onCancel={() => setView('list')} />;
  if (view === 'detail' && selected) return <CampaignDetail campaign={selected} headers={headers} onBack={() => { setView('list'); setSelected(null); fetch(); }} />;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Campaigns</h1>
          <p className="text-sm text-muted-foreground mt-1">Automated outreach sequences for your leads</p>
        </div>
        <Button onClick={() => setView('create')} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="create-campaign-btn">
          <Plus size={16} className="mr-1.5" /> New Campaign
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="animate-spin mr-2" size={18} /> Loading...</div>
      ) : campaigns.length === 0 ? (
        <Card className="border-border bg-card">
          <CardContent className="py-16 text-center">
            <Megaphone size={40} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No campaigns yet. Create your first campaign to start automated outreach.</p>
            <Button onClick={() => setView('create')} className="mt-4 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="empty-create-btn">
              <Plus size={14} className="mr-1" /> Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => {
            const meta = TYPE_META[c.type] || TYPE_META.sms_drip;
            const Icon = meta.icon;
            return (
              <div key={c.id} onClick={() => { setSelected(c); setView('detail'); }}
                className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:border-border cursor-pointer transition-colors"
                data-testid={`campaign-${c.id}`}>
                <div className={`h-10 w-10 rounded-lg ${meta.bg} flex items-center justify-center shrink-0`}>
                  <Icon size={20} className={meta.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground truncate">{c.name}</span>
                    <Badge className={`text-[10px] ${STATUS_BADGE[c.status]}`}>{c.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{meta.label} &middot; {c.steps?.length || 0} steps &middot; {c.stats?.enrolled || 0} enrolled</p>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                  {c.stats?.calls_made > 0 && <span className="flex items-center gap-1"><Phone size={11} /> {c.stats.calls_made}</span>}
                  {c.stats?.sms_sent > 0 && <span className="flex items-center gap-1"><MessageSquare size={11} /> {c.stats.sms_sent}</span>}
                  <ChevronRight size={16} className="text-muted-foreground" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ── Campaign Builder ──────────────────────────────────────────────────

function CampaignBuilder({ headers, onDone, onCancel }) {
  const [form, setForm] = useState({
    name: '', type: 'sms_drip', targeting: { temperature: [] },
    steps: [{ channel: 'sms', delay_hours: 0, template: '' }],
    schedule: { pace_per_hour: 10, business_hours_only: true },
    auto_enroll: false, auto_enroll_rules: {},
  });
  const [saving, setSaving] = useState(false);

  const addStep = () => setForm(f => ({ ...f, steps: [...f.steps, { channel: 'sms', delay_hours: 24, template: '' }] }));
  const removeStep = (i) => setForm(f => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));
  const updateStep = (i, field, value) => setForm(f => {
    const steps = [...f.steps];
    steps[i] = { ...steps[i], [field]: value };
    return { ...f, steps };
  });

  const toggleTemp = (temp) => {
    const current = form.targeting.temperature || [];
    const next = current.includes(temp) ? current.filter(t => t !== temp) : [...current, temp];
    setForm(f => ({ ...f, targeting: { ...f.targeting, temperature: next } }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Campaign name is required');
    if (!form.steps.length) return toast.error('Add at least one step');
    if (form.steps.some(s => !s.template.trim())) return toast.error('All steps need a message template');
    setSaving(true);
    try {
      await axios.post(`${API}/campaigns`, form, { headers });
      toast.success('Campaign created');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="campaign-builder">
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"><ArrowLeft size={18} /></button>
        <h1 className="text-xl font-bold text-foreground">New Campaign</h1>
      </div>

      <Card className="border-border bg-card">
        <CardContent className="pt-6 space-y-5">
          {/* Name + Type */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Campaign Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Q1 Hot Lead Blitz" className="mt-1.5 bg-muted border-border text-foreground" data-testid="campaign-name" />
            </div>
            <div>
              <Label className="text-foreground">Type</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                {Object.entries(TYPE_META).map(([key, meta]) => {
                  const Icon = meta.icon;
                  return (
                    <button key={key} type="button" onClick={() => setForm(f => ({ ...f, type: key }))}
                      className={`flex items-center gap-1.5 p-2 rounded-lg text-xs font-medium border transition-colors ${form.type === key ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-border text-muted-foreground hover:border-border'}`}
                      data-testid={`type-${key}`}>
                      <Icon size={13} className={meta.color} /> {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Targeting */}
          <div>
            <Label className="text-foreground">Target Leads</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {['hot', 'warm', 'cold'].map(t => (
                <button key={t} type="button" onClick={() => toggleTemp(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${(form.targeting.temperature || []).includes(t) ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-border text-muted-foreground hover:border-border'}`}
                  data-testid={`target-${t}`}>
                  <Target size={11} className="inline mr-1" />{t.charAt(0).toUpperCase() + t.slice(1)} Leads
                </button>
              ))}
            </div>
          </div>

          {/* Auto-enroll */}
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setForm(f => ({ ...f, auto_enroll: !f.auto_enroll, auto_enroll_rules: f.auto_enroll ? {} : { temperature: f.targeting.temperature, has_phone: true } }))}
              className={`h-5 w-9 rounded-full transition-colors relative ${form.auto_enroll ? 'bg-emerald-500' : 'bg-accent'}`} data-testid="auto-enroll-toggle">
              <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-card transition-transform ${form.auto_enroll ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
            <span className="text-sm text-foreground">Auto-enroll new matching leads</span>
          </div>

          {/* Schedule */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Pace (leads/hour)</Label>
              <Input type="number" value={form.schedule.pace_per_hour} onChange={e => setForm(f => ({ ...f, schedule: { ...f.schedule, pace_per_hour: parseInt(e.target.value) || 10 } }))} className="mt-1.5 bg-muted border-border text-foreground" data-testid="pace-input" />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input type="checkbox" checked={form.schedule.business_hours_only} onChange={e => setForm(f => ({ ...f, schedule: { ...f.schedule, business_hours_only: e.target.checked } }))} className="rounded border-border bg-muted text-emerald-500" />
                Business hours only (9am-9pm)
              </label>
            </div>
          </div>

          {/* Steps */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-foreground">Campaign Steps</Label>
              <button type="button" onClick={addStep} className="text-xs text-emerald-400 hover:text-primary flex items-center gap-1" data-testid="add-step-btn"><Plus size={12} /> Add Step</button>
            </div>
            <div className="space-y-3">
              {form.steps.map((step, i) => {
                const ChIcon = CHANNEL_ICON[step.channel] || MessageSquare;
                return (
                  <div key={step.delay_days + "-" + step.channel + "-" + i} className="rounded-lg border border-border bg-muted p-3 space-y-2" data-testid={`step-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-muted-foreground bg-muted rounded px-1.5 py-0.5">Step {i + 1}</span>
                      <div className="flex gap-1">
                        {['call', 'sms', 'email'].map(ch => {
                          const CI = CHANNEL_ICON[ch];
                          return (
                            <button key={ch} type="button" onClick={() => updateStep(i, 'channel', ch)}
                              className={`p-1.5 rounded text-xs ${step.channel === ch ? 'bg-emerald-500/10 text-emerald-400' : 'text-muted-foreground hover:text-foreground'}`}>
                              <CI size={13} />
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-1 ml-auto">
                        <Clock size={11} className="text-muted-foreground" />
                        <Input type="number" value={step.delay_hours} onChange={e => updateStep(i, 'delay_hours', parseInt(e.target.value) || 0)}
                          className="w-16 h-7 text-xs bg-muted border-border text-foreground" />
                        <span className="text-[10px] text-muted-foreground">hrs delay</span>
                      </div>
                      {form.steps.length > 1 && (
                        <button type="button" onClick={() => removeStep(i)} className="p-1 text-muted-foreground hover:text-red-400"><X size={13} /></button>
                      )}
                    </div>
                    <Textarea value={step.template} onChange={e => updateStep(i, 'template', e.target.value)}
                      placeholder={step.channel === 'call' ? 'Call context / talking points...' : 'Message template... Use {lead_name} for personalization'}
                      rows={2} className="text-xs bg-muted border-border text-foreground placeholder:text-muted-foreground resize-none" data-testid={`step-${i}-template`} />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onCancel} className="border-border text-foreground">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-campaign-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Create Campaign
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ── Campaign Detail ───────────────────────────────────────────────────

function CampaignDetail({ campaign: initialCampaign, headers, onBack }) {
  const [campaign, setCampaign] = useState(initialCampaign);
  const [stats, setStats] = useState(null);
  const [actionLoading, setActionLoading] = useState('');

  const refresh = useCallback(async () => {
    try {
      const [campRes, statsRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaign.id}`, { headers }),
        axios.get(`${API}/campaigns/${campaign.id}/stats`, { headers }),
      ]);
      setCampaign(campRes.data);
      setStats(statsRes.data);
    } catch { /* ignore */ }
  }, [campaign.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const doAction = async (action) => {
    setActionLoading(action);
    try {
      await axios.post(`${API}/campaigns/${campaign.id}/${action}`, {}, { headers });
      toast.success(`Campaign ${action === 'stop' ? 'stopped' : action + 'ed'}`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action}`); }
    finally { setActionLoading(''); }
  };

  const doDelete = async () => {
    try {
      await axios.delete(`${API}/campaigns/${campaign.id}`, { headers });
      toast.success('Campaign deleted');
      onBack();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot delete'); }
  };

  const meta = TYPE_META[campaign.type] || TYPE_META.sms_drip;
  const Icon = meta.icon;
  const s = stats || campaign.stats || {};

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="campaign-detail">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted" data-testid="back-btn"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-foreground">{campaign.name}</h1>
            <Badge className={STATUS_BADGE[campaign.status]}>{campaign.status}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{meta.label} &middot; {campaign.steps?.length || 0} steps</p>
        </div>
        <div className="flex gap-2">
          {campaign.status === 'draft' && <Button size="sm" onClick={() => doAction('start')} disabled={!!actionLoading} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="start-btn"><Play size={14} className="mr-1" /> Start</Button>}
          {campaign.status === 'active' && <Button size="sm" onClick={() => doAction('pause')} disabled={!!actionLoading} className="bg-amber-500 hover:bg-amber-600 text-foreground" data-testid="pause-btn"><Pause size={14} className="mr-1" /> Pause</Button>}
          {campaign.status === 'paused' && <Button size="sm" onClick={() => doAction('start')} disabled={!!actionLoading} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="resume-btn"><Play size={14} className="mr-1" /> Resume</Button>}
          {['active', 'paused'].includes(campaign.status) && <Button size="sm" variant="outline" onClick={() => doAction('stop')} disabled={!!actionLoading} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="stop-btn"><Square size={14} className="mr-1" /> Stop</Button>}
          {['draft', 'completed', 'paused'].includes(campaign.status) && <Button size="sm" variant="outline" onClick={doDelete} className="border-border text-muted-foreground hover:bg-red-500/10 hover:text-red-400" data-testid="delete-btn"><Trash2 size={14} /></Button>}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Enrolled', value: s.enrolled || 0, color: 'text-foreground' },
          { label: 'Active', value: s.active || 0, color: 'text-emerald-400' },
          { label: 'Completed', value: s.completed || 0, color: 'text-blue-400' },
          { label: 'Calls Made', value: s.calls_made || 0, color: 'text-amber-400' },
          { label: 'SMS Sent', value: s.sms_sent || 0, color: 'text-violet-400' },
        ].map(st => (
          <div key={st.label} className="rounded-lg border border-border bg-card p-3 text-center">
            <p className={`text-xl font-bold ${st.color}`}>{st.value}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{st.label}</p>
          </div>
        ))}
      </div>

      {/* Steps */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3"><CardTitle className="text-foreground text-sm">Campaign Steps</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(campaign.steps || []).map((step, i) => {
              const ChIcon = CHANNEL_ICON[step.channel] || MessageSquare;
              return (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted border border-border">
                  <div className="flex flex-col items-center gap-1 shrink-0">
                    <span className="text-[9px] font-bold text-muted-foreground">{i + 1}</span>
                    <div className={`h-8 w-8 rounded-lg ${meta.bg} flex items-center justify-center`}><ChIcon size={14} className={meta.color} /></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-foreground capitalize">{step.channel}</span>
                      {step.delay_hours > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Clock size={10} /> {step.delay_hours}h delay</span>}
                    </div>
                    <p className="text-xs text-muted-foreground">{step.template}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Enrollments */}
      {stats?.enrollments?.length > 0 && (
        <Card className="border-border bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-foreground text-sm">Enrollments ({stats.enrolled})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {stats.enrollments.map(e => (
                <div key={e.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted text-xs">
                  <Users size={13} className="text-muted-foreground shrink-0" />
                  <span className="text-foreground font-medium w-28 truncate">{e.lead_name}</span>
                  <span className="text-muted-foreground font-mono w-28">{e.lead_phone || 'No phone'}</span>
                  <span className="text-muted-foreground">Step {e.current_step + 1}/{campaign.steps?.length || 0}</span>
                  <Badge className={`text-[9px] ml-auto ${STATUS_BADGE[e.status]}`}>{e.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
